<template>
   <AppLayout>
      <template #main>
         <div class="relative isolate">
            <div class="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
               <div
                  class="relative px-6 lg:pb-20 pt-24 sm:pt-32 lg:static lg:px-8 lg:py-32"
               >
                  <div class="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
                     <h2
                        class="text-3xl font-bold tracking-tight text-gray-900"
                     >
                        {{ $t('contact.drop_us_a_question') }}
                     </h2>
                     <p class="mt-6 text-lg leading-8 text-gray-600">
                        {{ $t('contact.contact_explanation') }}
                     </p>
                     <dl
                        class="mt-10 space-y-4 text-base leading-7 text-gray-600"
                     >
                        <Socials text-size-class="text-sm" />
                     </dl>
                  </div>
               </div>

               <form
                  @submit.prevent="submitForm"
                  class="px-6 pb-24 pt-20 sm:pb-32 lg:px-8 lg:py-32"
               >
                  <div class="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
                     <div
                        class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2"
                     >
                        <div class="sm:col-span-2 grow flex flex-col space-y-1">
                           <InputLabel
                              class="font-semibold required"
                              :value="$t('contact.full_name')"
                           />
                           <InputText
                              size="small"
                              class="w-full"
                              v-model="contactForm.name"
                           />
                           <InputError
                              :message="contactForm.errors.name"
                           />
                        </div>
                        <div class="sm:col-span-2 grow flex flex-col space-y-1">
                           <InputLabel
                              class="font-semibold required"
                              :value="$t('contact.email')"
                           />
                           <InputText
                              size="small"
                              class="w-full"
                              type="email"
                              v-model="contactForm.email"
                           />
                           <InputError
                              :message="contactForm.errors.email"
                           />
                        </div>
                        <div class="sm:col-span-2 grow flex flex-col space-y-1">
                           <InputLabel
                              class="font-semibold required"
                              :value="$t('contact.phone_number')"
                           />
                           <PhoneInput v-model="contactForm.phone" />
                           <InputError
                              :message="contactForm.errors.phone"
                           />
                        </div>
                        <div class="sm:col-span-2 grow flex flex-col space-y-1">
                           <InputLabel
                              class="font-semibold required"
                              :value="$t('contact.message')"
                           />
                           <Textarea
                              size="small"
                              v-model="contactForm.message"
                              rows="4"
                              class="w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-primary-color sm:text-sm sm:leading-6"
                           />
                           <InputError
                              :message="
                                 contactForm.errors.message
                              "
                           />
                        </div>
                        <div class="sm:col-span-2 grow flex flex-col space-y-1">
                           <VueRecaptcha
                              ref="reCaptcha"
                              @verify="onVerifyCaptcha"
                              :sitekey="page.props.recaptcha_site_key"
                           />
                           <InputError
                              :message="
                                 contactForm.errors.g_recaptcha_response
                              "
                           />
                        </div>
                     </div>
                     <div class="mt-8 flex justify-end">
                        <Button
                           size="small"
                           type="submit"
                           :loading="contactForm.processing"
                           :disabled="contactForm.processing"
                           class="w-full"
                        >
                           {{ $t('contact.lets_talk') }}
                        </Button>
                     </div>
                  </div>
               </form>
            </div>
            <div class="pb-32">
               <SponsorsSection />
            </div>
         </div>
      </template>
   </AppLayout>
</template>

<script setup>
   import AppLayout from '@/Layouts/AppLayout.vue';
   import InputLabel from '@/Components/Forms/InputLabel.vue';
   import { useForm, usePage } from '@inertiajs/vue3';
   import InputError from '@/Components/Forms/InputError.vue';
   import { isEmpty } from 'lodash';
   import PhoneInput from '@/Components/Forms/PhoneInput.vue';
   import { VueRecaptcha } from 'vue-recaptcha';
   import { ref } from 'vue';
   import SponsorsSection from '@/Components/SponsorsSection.vue';
   import { Button, InputText, Textarea } from 'primevue';
   import Socials from '@/Components/Socials.vue';


   import { useToast } from 'primevue/usetoast';
   const toast = useToast();

   const contactForm = useForm({
      name: null,
      email: null,
      phone: null,
      message: null,
      g_recaptcha_response: null,
   });

   const reCaptcha = ref();
   const page = usePage();

   function submitForm () {
      contactForm.post(route('contact.store'), {
         preserveScroll: true,
         onSuccess: () => {
            contactForm.reset();
            reCaptcha.value.reset();
            const toastData = page.props.flash;
            if (!isEmpty(toastData)) {
               toast.add({
                  group: 'headless',
                  severity: 'custom',
                  type: toastData.type,
                  summary: toastData.title,
                  detail: toastData.message,
                  life: 3000,
               });
            }
         },
      });
   }

   const onVerifyCaptcha = (response) => {
      contactForm.g_recaptcha_response = response;
      contactForm.errors.g_recaptcha_response = null;
   };
</script>

<style scoped></style>
